import axiosClient from "utils/axios";
import { ApiList, ApiOptionList, ApiResponse } from "../../../types/api.types";
import {
  Amc,
  AddAmcRequest,
  AddExtraAmcVisitRequest,
  AmcItemPersistPayload,
  UpdateAmcRequest,
} from "features/Amc/types/amc.types";
import type { AmcVisit } from "features/AmcVisitLogs/types/amc-visit.types";

const AMC_BASE_PATH = "/amcs";

/** Query shape for GET /amcs (paginated list + filters; search matches code or id when term is a positive number). */
export type AmcListParams = {
  page?: number;
  limit?: number;
  search?: string;
  /** Comma-separated AmcStatus: DRAFT, READY_FOR_ACTIVATION, ACTIVE, COMPLETED, CANCELLED */
  status?: string;
  /** Comma-separated AmcSourceType: QUOTATION, MANUAL, RENEWAL, MIGRATION */
  sourceType?: string;
  clientId?: number;
  createdBy?: number;
  startDateFrom?: string;
  startDateTo?: string;
  endDateFrom?: string;
  endDateTo?: string;
  createdFrom?: string;
  createdTo?: string;
};

function buildAmcListQueryParams(
  input: AmcListParams,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  const setPositiveInt = (key: string, v: number | undefined) => {
    if (v != null && Number.isFinite(v) && Number(v) > 0) {
      params[key] = Number(v);
    }
  };
  const setNonEmpty = (key: string, v: string | undefined) => {
    if (v != null && String(v).trim() !== "") {
      params[key] = String(v).trim();
    }
  };

  if (input.page != null) params.page = input.page;
  if (input.limit != null) params.limit = input.limit;
  setNonEmpty("search", input.search);
  setNonEmpty("status", input.status);
  setNonEmpty("sourceType", input.sourceType);
  setPositiveInt("clientId", input.clientId);
  setPositiveInt("createdBy", input.createdBy);
  setNonEmpty("startDateFrom", input.startDateFrom);
  setNonEmpty("startDateTo", input.startDateTo);
  setNonEmpty("endDateFrom", input.endDateFrom);
  setNonEmpty("endDateTo", input.endDateTo);
  setNonEmpty("createdFrom", input.createdFrom);
  setNonEmpty("createdTo", input.createdTo);

  return params;
}

export const getAmcContracts = async (input: AmcListParams = {}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiList<Amc[]>>>(
      AMC_BASE_PATH,
      { params: buildAmcListQueryParams(input) },
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching AMCs  : ", err);
    throw err;
  }
};

export const getAmc = async (id: number) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.get<ApiResponse<Amc>>(
      `${AMC_BASE_PATH}/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error fetching AMC : ", err);
    throw err;
  }
};

export const createAmc = async (payload: AddAmcRequest) => {
  try {
    const response = await axiosClient.post<ApiResponse<Amc>>(
      AMC_BASE_PATH,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error creating AMC : ", err);
    throw err;
  }
};

export const addExtraAmcVisit = async (
  amcId: number,
  body: AddExtraAmcVisitRequest,
) => {
  try {
    if (!Number.isInteger(amcId) || amcId <= 0) {
      throw new Error("Invalid AMC id");
    }
    const response = await axiosClient.post<ApiResponse<AmcVisit>>(
      `${AMC_BASE_PATH}/${amcId}/extra-visits`,
      body,
    );
    return response.data;
  } catch (err) {
    console.warn("Error adding extra AMC visit : ", err);
    throw err;
  }
};

export const updateAmc = async (id: number, payload: UpdateAmcRequest) => {
  try {
    if (!Number.isInteger(id) || id <= 0) {
      throw new Error("Invalid ID");
    }
    const response = await axiosClient.put<ApiResponse<Amc>>(
      `${AMC_BASE_PATH}/${id}`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error updating AMC : ", err);
    throw err;
  }
};

export const deleteAmc = async (id: number) => {
  try {
    const response = await axiosClient.delete<ApiResponse<Amc>>(
      `${AMC_BASE_PATH}/${id}`,
    );
    return response.data;
  } catch (err) {
    console.warn("Error deleting AMC : ", err);
    throw err;
  }
};

export const getAmcOptions = async ({
  query,
  limit,
  page,
  status,
}: {
  query?: string;
  limit?: number;
  page?: number;
  /** e.g. `ACTIVE` — forwarded to GET /amcs/options when set. */
  status?: string;
}) => {
  try {
    const response = await axiosClient.get<ApiResponse<ApiOptionList>>(
      `${AMC_BASE_PATH}/options`,
      {
        params: {
          query,
          page,
          limit,
          ...(status != null && String(status).trim() !== ""
            ? { status: String(status).trim() }
            : {}),
        },
      },
    );

    return response.data;
  } catch (err) {
    console.warn("Error fetching AMC Options : ", err);
    throw err;
  }
};

export const markAmcReadyForActivation = async (
  id: number,
  payload?: { finalAmount?: number | null },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<Amc>>(
      `${AMC_BASE_PATH}/${id}/ready-for-activation`,
      payload ?? {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error marking AMC ready for activation : ", err);
    throw err;
  }
};

export const cancelAmc = async (id: number, cancelReason?: string | null) => {
  try {
    const response = await axiosClient.post<ApiResponse<Amc>>(
      `${AMC_BASE_PATH}/${id}/cancel`,
      cancelReason?.trim() ? { cancelReason: cancelReason.trim() } : {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error cancelling AMC : ", err);
    throw err;
  }
};

/**
 * ACTIVE → COMPLETED. Single manual action, callable any time (no end-date
 * gating). Blocked if any linked visit is still PLANNED/IN_PROGRESS. Takes no
 * body — the final invoiced amount is locked in earlier, at ready-for-activation.
 */
export const completeAmc = async (id: number) => {
  try {
    const response = await axiosClient.post<ApiResponse<Amc>>(
      `${AMC_BASE_PATH}/${id}/complete`,
      {},
    );
    return response.data;
  } catch (err) {
    console.warn("Error completing AMC : ", err);
    throw err;
  }
};

/**
 * Corrects billing for this contract's `finalAmount`. If no active invoice
 * currently exists, a new one is generated. If the active invoice already has
 * a payment or advance settlement applied, that same invoice's total is
 * corrected in place instead (same id/code, only its adjustment line and
 * total change — re-fetch invoices for this source afterward either way).
 * Requires ACTIVE or COMPLETED. Gated by amc.regenerate_invoice. Rejected if
 * an active invoice exists with nothing collected against it yet — cancel it
 * first via {@link cancelInvoice} (`features/Invoices/services/InvoiceService`) —
 * or if the new amount is less than what's already been collected.
 *
 * `items`, when provided, replaces the contract's own stored line items (not
 * just a one-off override on the invoice) — this is Accountant/Marketing's
 * only path to correct line items on a completed job, since Post-Completion
 * Editing otherwise restricts that to Super Admin. Omit it to leave existing
 * items untouched. Note: in the in-place-correction case, this only updates
 * the contract's own stored items — it does not retroactively rewrite the
 * already-issued invoice's other line items.
 */
export const regenerateAmcInvoice = async (
  id: number,
  payload: {
    finalAmount: number;
    reason?: string | null;
    items?: AmcItemPersistPayload[];
  },
) => {
  try {
    const response = await axiosClient.post<ApiResponse<Amc>>(
      `${AMC_BASE_PATH}/${id}/regenerate-invoice`,
      payload,
    );
    return response.data;
  } catch (err) {
    console.warn("Error regenerating AMC invoice : ", err);
    throw err;
  }
};
